/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// Brand wordmark, resolved per brand.
//
// `/images/logo.svg` is NOT a stock placeholder — it is the Neiman Marcus
// script wordmark, viewBox 0 0 588.4 211.9 (2.78:1), the same asset the NM
// header imports. It is LIVE'S OWN FILE, byte-for-byte: live inlines it as a
// base64 data URI on `img.header-brand-site-logo-container__site-logo`, and it
// decodes to sha256 a785e8e5006a91d876d7fb3819d4d7a0ae2f4f469174c54494769edad9c05117
// — identical in all five NM desktop page saves under
// .reference/live-shots/owner-2026-08-22/ that carry a header. It replaced a
// DIFFERENT drawing (`<svg id="neiman_marcus">`, viewBox 0 0 348.95 134.14,
// 2.60:1, two paths, 19,358 bytes against live's four paths and 6,554), which
// rendered the wordmark ink 4.71px taller than live's at the shared 194x76 box
// and was visible on the owner's flicker test. Until this component resolved
// a brand it painted that mark unconditionally, so every consumer showed the
// Neiman Marcus name on Bergdorf Goodman: measured on the BG 404 at
// clientWidth 390 and 1280, and on the BG sign-in page. That is a brand LEAK,
// not an unfinished placeholder, which is why a fix that merely swapped in a
// neutral asset would have been wrong.
//
// TWO brand sources, in this order, because neither alone covers both consumers:
//
//   1. the `brand` prop — REQUIRED by the root ErrorBoundary. It renders inside
//      `Layout` but OUTSIDE the provider stack in root.tsx's `App` default
//      export, which is where `SiteProvider` is mounted. `useSite()` therefore
//      resolves to nothing there and the hook below silently returns
//      DEFAULT_BRAND on BOTH brands — green tests, live bug. The boundary knows
//      the answer (`brandForSite(rootData?.site)`) and hands it down.
//   2. `useBrand()` — for consumers that DO sit inside the provider stack and
//      pass no prop (the auth surfaces under `_empty`). SSR-safe, so the server
//      and the client agree.
//
// Deliberately NOT a source: `document.documentElement.dataset.brand`. It is
// absent during SSR, so the server would paint NM and the client would swap to
// BG — a hydration mismatch on every BG error page.
//
// The site header does not consume this component. It needs a stacked/wide
// swap by width that a single-asset component does not model.
import { useBrand } from '@/lib/brand/use-brand';
import type { Brand } from '@/lib/brand/brand';
import logo from '/images/logo.svg';
import bgLogo from '/images/bg/bg-wordmark.svg';

/**
 * Per-brand mark and its alt text.
 *
 * BG gets the WIDE lockup at every width here, not the stacked one. Consumers
 * of this component size the art small — the error header is 12px tall, 16px at
 * `lg` — and the stacked mark is 2.69:1, so it would paint ~32x12: a two-line
 * lockup far too small to read. The wide lockup is 11.71:1, giving ~140x12 and
 * ~187x16, which fits comfortably in an otherwise-empty header even at 390.
 * Live's own error-page treatment is UNMEASURED — no capture of it exists —
 * so this matches OUR brand system rather than live.
 *
 * The alt is the brand's own name rather than a translation key, and that is a
 * constraint rather than a preference: the root ErrorBoundary builds an
 * isolated i18next instance carrying ONLY the `routeError` namespace, and its
 * loader-never-ran branch has no i18next at all — so no key reachable from
 * there exists to look up. These are also proper nouns; several locales
 * translate the existing `header.logoAlt` entry to "Home" ("Accueil",
 * "Startseite"), which is wrong for a wordmark. The header already spells the
 * BG name as a literal for the same reason.
 *
 * INTRINSIC DIMENSIONS ARE PART OF THE MARK, not of any one consumer.
 *
 * Every consumer sizes this art with `h-* w-auto`, so the rendered width is
 * derived from the image's own aspect ratio — which the browser does not know
 * until the SVG has loaded. Without `width`/`height` the <img> lays out at zero
 * width and then jumps to its real width when the asset lands. On the root
 * ErrorBoundary that is the whole visible content of the header, and an error
 * page is the worst surface on which to also shift.
 *
 * The numbers are each asset's own viewBox, read from the file rather than
 * assumed, and they are DIFFERENT SHAPES — which is why they belong here beside
 * the `src` and not on a `<Logo>` call site. A single pair hardcoded by a
 * consumer would be right for one brand and wrong for the other:
 *
 *   public/images/logo.svg            viewBox="0 0 588.4 211.9"    ->  2.78:1
 *   public/images/bg/bg-wordmark.svg  viewBox="0 0 398 34"         -> 11.71:1
 *
 * Both ratios corroborate the two figures already cited in the comment above,
 * which were measured independently. Rounded to integers because HTML dimension
 * attributes parse as integers — 588/212 differs from the true ratio by 0.06%,
 * which is 0.02px at this component's largest rendered size (16px tall).
 */
const BRAND_MARK: Record<Brand, { src: string; alt: string; width: number; height: number }> = {
    nm: { src: logo, alt: 'Neiman Marcus', width: 588, height: 212 },
    bg: { src: bgLogo, alt: 'Bergdorf Goodman', width: 398, height: 34 },
};

interface LogoProps {
    className?: string;
    /** Overrides the brand-derived alt. Omit to get the brand's own name. */
    alt?: string;
    /**
     * Active brand. Omit inside the provider stack — it resolves from site
     * context. Required where that context is unavailable, which today means
     * the root ErrorBoundary.
     */
    brand?: Brand;
}

export default function Logo({ className, alt, brand }: LogoProps) {
    // Called unconditionally so hook order is stable; the prop wins when given.
    const contextBrand = useBrand();
    const mark = BRAND_MARK[brand ?? contextBrand];
    return <img src={mark.src} alt={alt ?? mark.alt} width={mark.width} height={mark.height} className={className} />;
}
